import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { CustomerCombobox } from "@/components/common/CustomerCombobox";
import { ProductCombobox } from "@/components/common/ProductCombobox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const VAT = ["19", "7", "0"];
const CATS = ["S", "Z", "E", "AE"];
const DEFAULT_INTRODUCTION_TEXT = "Danke, dass Sie unseren Service in Anspruch genommen haben.";
const VAT_BY_CLASS = {
  standard: { vat_rate: "19", tax_category: "S" },
  reduced: { vat_rate: "7", tax_category: "S" },
  zero: { vat_rate: "0", tax_category: "Z" },
  exempt: { vat_rate: "0", tax_category: "E", exemption_reason: "Steuerbefreit gemäß Produktstammdaten" },
};
const emptyLine = () => ({ product_id: "", product_type: "", sku: "", image_url: "", description: "", quantity: "1", unit_price: "0.00", vat_rate: "19", tax_category: "S", exemption_reason: "" });

export default function EInvoiceEditor() {
  const { t } = useI18n();
  const { modules } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [customerId, setCustomerId] = useState("");
  const [customerLabel, setCustomerLabel] = useState("");
  const [invoiceFormat, setInvoiceFormat] = useState("standard");
  const [buyerRef, setBuyerRef] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState([emptyLine()]);
  const [vehicleId, setVehicleId] = useState("");
  const [introductionText, setIntroductionText] = useState(DEFAULT_INTRODUCTION_TEXT);
  const [closingText, setClosingText] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: invoice } = useQuery({
    queryKey: ["einvoice", id],
    queryFn: async () => (await api.get(`/einvoice/invoices/${id}`)).data,
    enabled: isEdit,
  });
  const { data: vehicleData = { items: [] } } = useQuery({ queryKey: ["invoice-customer-vehicles", customerId], enabled: modules?.includes("automotive_workshop") && !!customerId, queryFn: async () => (await api.get("/automotive/vehicles", { params: { customer_id: customerId, page_size: 100 } })).data });
  useEffect(() => {
    if (!invoice) return;
    setCustomerId(invoice.customer_id || "");
    setCustomerLabel(invoice.buyer_name || "");
    setInvoiceFormat(invoice.invoice_format || "electronic");
    setBuyerRef(invoice.buyer_reference || "");
    setIssueDate(invoice.issue_date || new Date().toISOString().slice(0, 10));
    setVehicleId(invoice.vehicle_id || "");
    setIntroductionText(invoice.introduction_text || ""); setClosingText(invoice.closing_text || ""); setNotes(invoice.notes || "");
    setLines((invoice.totals?.lines || [emptyLine()]).map((line) => ({
      product_id: line.product_id || "",
      product_type: line.product_type || "",
      sku: line.sku || "",
      image_url: line.image_url || "",
      description: line.description || "",
      quantity: String(line.quantity ?? "1"),
      unit_price: String(line.unit_price ?? "0.00"),
      vat_rate: String(line.vat_rate ?? "19"),
      tax_category: line.tax_category || "S",
      exemption_reason: line.exemption_reason || "",
    })));
  }, [invoice]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const selectProduct = (i, product) => {
    const tax = VAT_BY_CLASS[product.vat_class] || VAT_BY_CLASS.standard;
    setLines((current) => current.map((line, index) => index === i ? {
      ...line,
      product_id: product.id,
      product_type: product.product_type || "product",
      sku: product.sku || "",
      image_url: product.image_url || "",
      description: product.title || line.description,
      unit_price: String(product.effective_price || product.selling_price || "0.00"),
      ...tax,
    } : line));
  };
  const net = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const submit = async () => {
    if (!customerId) { toast.error(t("einvoice.pick_customer")); return; }
    setSaving(true);
    try {
      const body = { customer_id: customerId, invoice_format: invoiceFormat, buyer_reference: buyerRef, issue_date: issueDate, doc_type: "invoice", vehicle_id: vehicleId || null, introduction_text: introductionText, closing_text: closingText, notes,
        lines: lines.map((l) => ({ product_id: l.product_id || null, product_type: l.product_type || null, sku: l.sku || null, description: l.description, quantity: l.quantity, unit_price: l.unit_price, vat_rate: l.vat_rate, tax_category: l.tax_category, exemption_reason: l.exemption_reason || null })) };
      const { data } = isEdit
        ? await api.put(`/einvoice/invoices/${id}`, body)
        : await api.post("/einvoice/invoices", body);
      toast.success(t(isEdit ? "einvoice.updated" : "einvoice.created"));
      navigate(`/finance/einvoices/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate(isEdit ? `/finance/einvoices/${id}` : "/finance/einvoices")} data-testid="einvoice-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>
      <PageHeader title={t(isEdit ? "einvoice.edit_draft" : "einvoice.new")} subtitle={t(isEdit ? "einvoice.edit_draft_subtitle" : "einvoice.new_subtitle")} />
      <Card className="mb-4 p-4">
        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <Label>{t("einvoice.invoice_format")}</Label>
            <Select value={invoiceFormat} onValueChange={setInvoiceFormat}>
              <SelectTrigger data-testid="einvoice-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">{t("einvoice.invoice_format_standard")}</SelectItem>
                <SelectItem value="electronic">{t("einvoice.invoice_format_electronic")}</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-500">{t(invoiceFormat === "standard" ? "einvoice.invoice_format_standard_hint" : "einvoice.invoice_format_electronic_hint")}</p>
          </div>
          <div>
            <Label>{t("einvoice.customer")}</Label>
            <CustomerCombobox
              value={customerId}
              label={customerLabel}
              onSelect={(customer) => {
                setCustomerId(customer.id);
                setCustomerLabel(customer.title);
                setVehicleId("");
              }}
              testId="einvoice-customer-select"
            />
          </div>
          <div>
            <Label>{t("einvoice.buyer_reference")}</Label>
            <Input value={buyerRef} onChange={(e) => setBuyerRef(e.target.value)} placeholder="04011000-12345-34" data-testid="einvoice-buyerref-input" />
            <p className="mt-1 text-xs text-slate-500">{t("einvoice.buyer_reference_hint")}</p>
          </div>
          <div>
            <Label>{t("einvoice.date")}</Label>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="einvoice-date-input" />
          </div>
          {modules?.includes("automotive_workshop") && customerId && <div><Label>{t("automotive.vehicle")}</Label><Select value={vehicleId || "none"} onValueChange={(v) => setVehicleId(v === "none" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">{t("automotive.no_vehicle_general_quote")}</SelectItem>{vehicleData.items.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_plate} · {v.make} {v.model}</SelectItem>)}</SelectContent></Select></div>}
        </div>
      </Card>

      <Card className="mb-4 grid gap-4 p-4 md:grid-cols-2"><div><Label>{t("automotive.invoice_intro")}</Label><Textarea value={introductionText} onChange={(e) => setIntroductionText(e.target.value)} rows={3} placeholder={t("automotive.invoice_intro_hint")} /></div><div><Label>{t("automotive.invoice_closing")}</Label><Textarea value={closingText} onChange={(e) => setClosingText(e.target.value)} rows={3} placeholder={t("automotive.invoice_closing_hint")} /></div><div className="md:col-span-2"><Label>{t("common.notes")}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div></Card>

      <Card className="p-4">
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-3 dark:border-slate-800" data-testid={`einvoice-line-${i}`}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div><Label className="text-xs">{t("quotes.select_product")}</Label><ProductCombobox lineType="" value={l.product_id} label={l.product_id ? `${l.sku ? `${l.sku} · ` : ""}${l.description}` : ""} imageUrl={l.image_url} customerId={customerId} onSelect={(product) => selectProduct(i, product)} testId={`line-product-${i}`} /></div>
                <div><Label className="text-xs">{t("einvoice.line_desc")}</Label><Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} /></div>
              </div>
              <div className="mt-2 grid grid-cols-12 items-end gap-2">
              <div className="col-span-4 sm:col-span-2"><Label className="text-xs">{t("einvoice.line_qty")}</Label>
                <Input value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="tabular-nums" data-testid={`line-qty-${i}`} /></div>
              <div className="col-span-5 sm:col-span-3"><Label className="text-xs">{t("einvoice.line_price")}</Label>
                <Input value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="tabular-nums" data-testid={`line-price-${i}`} /></div>
              <div className="col-span-4 sm:col-span-2"><Label className="text-xs">{t("einvoice.line_vat")}</Label>
                <Select value={l.vat_rate} onValueChange={(v) => setLine(i, "vat_rate", v)}>
                  <SelectTrigger data-testid={`line-vat-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{VAT.map((v) => <SelectItem key={v} value={v}>{v}%</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-4 sm:col-span-2"><Label className="text-xs">{t("einvoice.line_cat")}</Label>
                <Select value={l.tax_category} onValueChange={(v) => setLine(i, "tax_category", v)}>
                  <SelectTrigger data-testid={`line-cat-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                <Button variant="ghost" size="icon" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} disabled={lines.length === 1} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} data-testid="einvoice-add-line"><Plus className="h-4 w-4" />{t("einvoice.add_line")}</Button>
          <div className="text-sm text-slate-500">{t("einvoice.net_preview")}: <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{net.toFixed(2)} €</span></div>
        </div>
      </Card>

      <div className="mt-4 flex justify-end">
        <Button onClick={submit} disabled={saving} data-testid="einvoice-save-button">{t(isEdit ? "common.save" : "einvoice.create_draft")}</Button>
      </div>
    </div>
  );
}
